import { GroupType } from '@web/groups/types';

import SelectableSearchableSortableListCtrl from '../SelectableSearchableSortableListCtrl';
import { groupTexts } from '../constants';

import ArchiveGroupCtrl from './ArchiveGroupCtrl';
import archiveUrl from './archive.html';

class ManageGroupsCtrl extends SelectableSearchableSortableListCtrl {
  constructor(
    PersonaCollection,
    ConfirmService,
    Restangular,
    RestUtils,
    Authentication,
    AlertService,
    $state,
    $parse,
    $uibModal,
    filterFilter,
    uppercaseFirstFilter
  ) {
    'ngInject';

    super(filterFilter, $parse, 'name', (invalidate) => {
      PersonaCollection.onChange(invalidate);
    });

    Object.assign(this, {
      PersonaCollection,
      ConfirmService,
      Restangular,
      RestUtils,
      Authentication,
      AlertService,
      $state,
      $parse,
      $uibModal,
      filterFilter,
      uppercaseFirstFilter,
    });
  }

  $onInit() {
    this.groupTypePlural = `${this.groupType}s`;
    this.sortMethod = 'name';

    Object.defineProperty(this, this.groupTypePlural, {
      get() {
        return this.items;
      },
      set(value) {
        this.items = value;
      },
    });

    this.search = '';
    this.selectedItemIds = Object.create(null);
  }

  get user() {
    return this.Authentication.user;
  }

  get items() {
    if (this.displayMyGroups) {
      return this.PersonaCollection.me[this.groupTypePlural] || [];
    }
    return this.PersonaCollection[this.groupTypePlural] || [];
  }

  deleteSelectedGroups() {
    this.ConfirmService.ask(
      {
        question: groupTexts[this.groupType].multipleDeletion.title,
        conditions: groupTexts[this.groupType].multipleDeletion.conditions,
        title: `Delete ${this.groupType}s`,
        confirm: 'Delete',
        warning: 'Warning: this cannot be undone.',
      },
      () => {
        const ids = Object.keys(this.selectedItemIds).filter(
          (groupId) =>
            this.selectedItemIds[groupId] &&
            groupId in this.visibleItemsDict &&
            this.canDeleteGroup(groupId)
        );
        this.Restangular.all('groups')
          .customDELETE(null, { id: ids })
          .then(() => {
            this._successDelete(this.selectedItemIds);
            this.selectedItemIds = Object.create(null);
          });
      }
    ).ignoreCancel();
  }

  _successDelete(selected) {
    if (!selected) {
      return this._addSingleDeleteNotification();
    }

    const number = Object.keys(selected).filter((key) => selected[key]).length;
    const isPlural = number > 1;
    if (!isPlural) {
      return this._addSingleDeleteNotification();
    }

    const text = `${number} ${this.groupTypePlural} deleted`;
    this.AlertService.success(text);
  }

  _addSingleDeleteNotification() {
    if (this.groupType === GroupType.archivedLocation) {
      this.AlertService.success('Archived location deleted');
    } else if (this.groupType === GroupType.archivedPosition) {
      this.AlertService.success('Archived position deleted');
    } else {
      this.AlertService.success(`${this.uppercaseFirstFilter(this.groupType)} deleted`);
    }
  }

  deleteItem(group, groupName) {
    this.ConfirmService.ask(
      {
        question: groupTexts[this.groupType].singleDeletion.title,
        conditions: groupTexts[this.groupType].singleDeletion.conditions,
        title: `Delete ${groupName || this.groupType}`,
        warning: 'Warning: this cannot be undone.',
        confirm: 'Delete',
        confirmText: groupTexts[this.groupType].singleDeletion?.confirmText,
      },
      () => {
        group.remove().then(() => {
          this._addSingleDeleteNotification();
        });
      }
    ).ignoreCancel();
  }

  async archiveItem(group) {
    const params = await this.$uibModal.open({
      templateUrl: archiveUrl,
      controller: ArchiveGroupCtrl,
      controllerAs: 'ctrl',
      size: '620',
      resolve: {
        groups: () => [group],
      },
    }).result;

    await this.PersonaCollection.archiveGroups([group], params);
    this.AlertService.success(`${this.uppercaseFirstFilter(this.groupType)} archived`);
  }

  async unarchiveItem(group, baseType) {
    await this.ConfirmService.ask({
      question: groupTexts[this.groupType].unarchive.title,
      title: `Unarchive ${baseType || this.groupType}`,
      type: 'primary',
      confirm: 'Unarchive',
    });

    await this.PersonaCollection.unarchiveGroups([group]);
    this.AlertService.success(
      `${this.uppercaseFirstFilter(baseType || this.groupType)} unarchived`
    );
  }

  get currentStateName() {
    return this.$state.current.name;
  }

  isColumnVisible(columnName) {
    if (!this.columnSubset) {
      return true;
    }

    return this.columnSubset.includes(columnName);
  }

  canDeleteGroup() {
    return true; // this logic can be overriden in controllers that extend from this controller class
  }
}

export default ManageGroupsCtrl;
